import { DateInput } from "@/components/DateInput";


const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const lines = (t, min) => {
  const l = (t || "").replace(/(\n\s*)+$/, "").split("\n");
  if (l.length === 1 && !l[0]) l.pop();
  return [...l, ...Array(Math.max(0, min - l.length)).fill("")];
};
const inputCls = "w-full min-w-0 bg-transparent outline-none focus:bg-yellow-50 print:bg-transparent font-bold";

const Cell = ({ label, k, f, set, pick, type = "text", className = "" }) => {
  const value = f[k] ?? "";
  return (
    <div onClick={pick && (() => pick(k))} className={`border border-neutral-500 px-1.5 py-px min-w-0 ${pick ? "cursor-pointer active:bg-yellow-100" : ""} ${className}`} data-testid={pick ? `bodyshop-pick-${k}` : undefined}>
      <div className="text-[6.5px] uppercase tracking-wider text-neutral-600 font-bold leading-tight">{label}</div>
      {pick ? (
        <div className="text-[13px] font-bold truncate min-h-[17px] leading-[17px]">{(type === "date" ? fmt(value) : value) || <span className="text-neutral-300 font-normal print:hidden">tocca per scrivere</span>}</div>
      ) : set && type === "date" ? (
        <DateInput value={value} onChange={(e) => set(k, e.target.value)} data-testid={`bodyshop-${k}`} aria-label={label}
          className="block !p-0 border-0 h-[17px] text-[13px] font-bold leading-none rounded-none bg-transparent text-black focus:ring-0 focus:bg-amber-50 placeholder:text-neutral-400 placeholder:font-normal print:placeholder:text-transparent" />
      ) : set ? (
        <input value={value} onChange={(e) => set(k, e.target.value)} data-testid={`bodyshop-${k}`}
          className={`${inputCls} block p-0 border-0 text-[13px] h-[17px] leading-none`} />
      ) : (
        <div className="text-[13px] font-bold truncate min-h-[17px] leading-[17px]">{type === "date" ? fmt(value) : value}</div>
      )}
    </div>
  );
};

const focusNext = (e) => {
  if (e.key !== "Enter") return;
  e.preventDefault();
  e.currentTarget.closest("li")?.nextElementSibling?.querySelector("input")?.focus();
};

const List = ({ title, items, testid, compact, grow = "flex-1", row = "leading-[21px]", onEdit, onPick }) => (
  <div onClick={onPick} className={`${grow} min-h-0 flex flex-col overflow-hidden ${onPick ? "cursor-pointer active:bg-yellow-50" : ""}`} data-testid={testid}>
    <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] bg-neutral-900 text-white px-1.5 py-px">{title}</div>
    <ol className="flex-1">
      {items.map((t, i) => (
        <li key={i} className={`flex gap-1.5 border-b border-neutral-300 px-1 ${compact ? "text-[10px] leading-[14px]" : `text-[12.5px] ${row}`} font-bold`}>
          <span className="text-neutral-400 w-5 shrink-0 text-right font-semibold">{i + 1}.</span>
          {onEdit && !onPick ? (
            <input value={t} onChange={(e) => onEdit(i, e.target.value)} onKeyDown={focusNext} className={`flex-1 ${inputCls}`} data-testid={`${testid}-row-${i + 1}`} />
          ) : <span className="break-words min-w-0">{t}</span>}
        </li>
      ))}
    </ol>
  </div>
);

const OperatorsColumn = ({ live, onPick }) => (
  <div onClick={onPick} className={`w-[60mm] shrink-0 min-h-0 flex flex-col overflow-hidden border-l border-neutral-300 ${onPick ? "cursor-pointer hover:bg-yellow-50 active:bg-yellow-100 print:hover:bg-transparent" : ""}`} data-testid="bodyshop-office-operators">
    <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] bg-neutral-900 text-white px-1.5 py-px">Operatori · ore</div>
    <ol>
      {[...(live?.operators || []), ...Array(Math.max(0, 4 - (live?.operators?.length || 0))).fill(null)].map((o, i) => (
        <li key={o ? o.name : `e${i}`} className="flex justify-between gap-1 border-b border-neutral-300 px-1.5 text-[13px] leading-[27px] min-h-[28px] font-bold">
          <span className="truncate">{o?.name}</span><span className="shrink-0">{o ? `${o.hours.toLocaleString("it-IT")} h` : ""}</span>
        </li>
      ))}
    </ol>
    <div className="flex justify-between bg-neutral-100 border-y border-neutral-900 px-1 text-[13px] leading-[24px] font-extrabold">
      <span>Totale</span><span data-testid="bodyshop-office-operators-total">{(live?.hours || 0).toLocaleString("it-IT")} h</span>
    </div>
    <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] bg-neutral-900 text-white px-1.5 py-px mt-2">Materiali</div>
    <ol className="flex-1 overflow-hidden" data-testid="bodyshop-office-materials">
      {(live?.materials || []).map((m, i) => (
        <li key={i} className="flex justify-between gap-1 border-b border-neutral-300 px-1 text-[11px] leading-[20px] font-bold">
          <span className="truncate">{m.description}</span><span className="shrink-0 max-w-[45%] truncate">{m.qty}</span>
        </li>
      ))}
    </ol>
  </div>
);

export const countLines = (f) => lines(f.works, 0).length + lines(f.parts, 0).length;
export const countParts = (f) => lines(f.parts, 0).length;

export const BodyshopCopy = ({ f, office = false, set, live, onPick, onHours }) => {
  const s = office ? set : undefined;
  const p = office ? onPick : undefined;
  const oh = office ? onHours : undefined;
  const pk = (k) => p && (() => p(k));
  const compact = lines(f.works, 0).length > 13 || countParts(f) > 3;
  const edit = (k, min) => s && ((i, v) => {
    const arr = lines(f[k], min);
    arr[i] = v;
    s(k, arr.join("\n").replace(/(\n\s*)+$/, ""));
  });
  const c = { f, set: s, pick: p };
  return (
  <div className="w-1/2 h-full px-[7mm] py-[6mm] flex flex-col gap-1.5 overflow-hidden" data-testid={office ? "bodyshop-copy-office" : "bodyshop-copy-car"}>
    <div className="flex items-start justify-between gap-2 border-b-2 border-neutral-900 pb-1">
      <div className="min-w-0">
        <img src="/logo.png" alt="Centro Auto Pontedera" className="h-7 w-auto" />
        <div className="text-[8px] text-neutral-600 mt-0.5">Carrozzeria · Via Don Primo Mazzolari 34/38, Pontedera (PI) · 0587 484791</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[12px] font-extrabold uppercase leading-tight">Scheda accettazione</div>
        <div className={`text-[9px] font-bold uppercase px-1.5 py-0.5 mt-0.5 inline-block ${office ? "bg-[#E63946] text-white" : "bg-neutral-900 text-white"}`}>{office ? "Copia ufficio" : "Copia veicolo"}</div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-1">
      <Cell {...c} label="N. scheda" k="number" className="bg-neutral-100" />
      <Cell {...c} label="Data ingresso" k="date_in" type="date" />
      <Cell {...c} label="Consegna prevista" k="date_out" type="date" />
      <Cell {...c} label="Marca" k="brand" />
      <Cell {...c} label="Modello" k="model" />
      <Cell {...c} label="Targa" k="plate" className="bg-neutral-100" />
      <Cell {...c} label="Telaio" k="vin" />
      <Cell {...c} label="Colore" k="color" />
      <Cell {...c} label="Km" k="km" />
    </div>
    <div className="grid grid-cols-[2fr_1fr] gap-1">
      <Cell {...c} label="Cliente" k="customer_name" />
      <Cell {...c} label="Telefono" k="customer_phone" />
    </div>
    {office ? (
      <div className="flex-[4] min-h-0 flex gap-1">
        <List title="Elenco lavori" grow="flex-1" compact={compact} row="leading-[28px]" items={lines(f.works, compact ? 0 : 13)} onEdit={edit("works", 13)} onPick={pk("works")} testid="bodyshop-office-works" />
        <OperatorsColumn live={live} onPick={oh} />
      </div>
    ) : (
      <List title="Elenco lavori" grow="flex-[4]" compact={compact} row="leading-[28px]" items={lines(f.works, compact ? 0 : 13)} testid="bodyshop-car-works" />
    )}
    <List title="Lista ricambi" grow="flex-1" compact={compact} items={lines(f.parts, compact ? 0 : 3)} onEdit={edit("parts", 3)} onPick={pk("parts")} testid={office ? "bodyshop-office-parts" : "bodyshop-car-parts"} />
    {office && (
      <div className="grid grid-cols-3 gap-1" data-testid="bodyshop-office-totals">
        <Cell f={{ h: live ? `${live.hours.toLocaleString("it-IT")} h` : "" }} pick={oh && live && oh} label="Ore lavorate" k="h" className="bg-neutral-100" />
        <Cell {...c} label="Preventivo € (+IVA)" k="quote_total" className="bg-neutral-100 text-right [&_input]:text-right" />
        <Cell f={{ t: live ? ((v) => { const [i, d] = v.toFixed(2).split("."); return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d}`; })(live.total) : "" }} pick={p && live && (() => p("_consuntivo"))} label="Consuntivo € (+IVA)" k="t" className="bg-neutral-100 text-right" />
      </div>
    )}
  </div>
  );
};
